import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { UtilsService } from "../../../../auth/service/utils.service";
import { Subject } from "rxjs";
import { IContentHeader } from "../../../../layout/components/content-header/content-header.component";
import { takeUntil } from "rxjs/operators";
import { IInvoice } from "../../interfaces/invoice.interface";
import { InvoiceService } from "../../invoice.service";
import { FormArray, FormGroup } from "@angular/forms";
import moment, { utc } from "moment";

@Component({
  selector: "app-invoice-create",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./invoice-create.component.html"
})
export default class InvoiceCreateComponent implements OnInit, OnDestroy {
  contentHeader: IContentHeader;
  analytics: IInvoice[] = [];
  selectedAnalyticsIds: number[] = [];
  expireInDays: number = 30;
  selectableOrders = [];
  invoiceForm: FormGroup;
  showErrors = false;
  rows_types: any = [];
  document_codes: { id: number; name: string; other: { code: string } }[] = [];
  payment_measure_units: any = [];
  invoice_document_types: any = [];
  credit_note_types: any = [];
  payment_terms: any = [];
  invoice_services: any = [];
  customersList: any = [];
  parent_orders = [];
  attachments_list: any = [];
  usersNames: string[] = [];
  canAddRow = true;
  months = moment.months();
  years = Array.from({ length: moment().year() - 2023 }, (_, i) => i + 2024);
  order_residue: number = -1;
  rowsToManipulate: number[] = [];
  clickedOnSave: boolean = true;
  users = [];
  products = [];
  isBlank = true;
  residual_count: { analytics: number; residual: number }[] = [];

  private _unsubscribeAll: Subject<any>;
  private defaultRowType: number | undefined;
  private defaultServiceTrack: any;

  constructor(
    public _invoiceService: InvoiceService,
    private activatedRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private router: Router,
    public _utilsService: UtilsService
  ) {
    this._utilsService.disableAutoHideLoading();
    // Set the private defaults
    this._unsubscribeAll = new Subject();
    this.activatedRoute.params
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((params) => {
        if (!!params.analytics) {
          this.selectedAnalyticsIds = params.analytics.split("_").map((id: string) => Number(id));
          this.isBlank = !this.selectedAnalyticsIds.length;
        }
      });

    this.contentHeader = {
      headerTitle: "CREATE_INVOICE",
      actionButton: false,
      breadcrumb: {
        type: "",
        links: [
          {
            name: "Home",
            isLink: true,
            link: "/"
          },
          {
            name: "MENU.INVOICES",
            isLink: false
          },
          {
            name: "MENU.INVOICES_TO_GENERATE",
            isLink: true,
            link: "/dashboard/invoice/to-generate"
          },
          {
            name: "CREATE_INVOICE",
            isLink: false
          }
        ]
      }
    };
  }

  async ngOnInit() {
    this._utilsService.showLoading();

    const itemsForLists = await this._invoiceService.itemsForLists();

    this.users = itemsForLists.users;
    this.products = itemsForLists.products;
    this.document_codes = itemsForLists.document_codes;
    this.customersList = itemsForLists.customers;
    this.rows_types = itemsForLists.rows_types;
    this.payment_measure_units = itemsForLists.payment_measure_units;
    this.payment_terms = itemsForLists.payment_terms;
    this.invoice_services = itemsForLists.invoice_services;
    this.invoice_document_types = itemsForLists.invoice_document_types;
    this.credit_note_types = itemsForLists.credit_note_types;

    this._invoiceService.getToGenerate().pipe(takeUntil(this._unsubscribeAll))
      .subscribe((invoices: IInvoice[]) => {
        this._utilsService.hideLoading();
        this.analytics = invoices.filter((invoice: IInvoice) => this.selectedAnalyticsIds.includes(invoice.analytics_id));
        this.cdr.detectChanges();
      });

    this.invoiceForm = this._invoiceService.buildForm(this.isBlank);

    setTimeout(() => this.processInvoices(), 1000);
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  processInvoices() {
    const invoice: any = this.analytics[0];

    this.defaultRowType = this.rows_types.find((type: any) => !!type.other.default)?.id;
    this.defaultServiceTrack = this.invoice_services.find((service: any) => !!service.other.default) || {};

    if (invoice) {
      for (const ord of (invoice.orders_child || [])) {
        this.selectableOrders.push({
          id: ord.id,
          parent: ord.parent_id,
          order_number: ord.order_number,
          description: ord.order_description,
          order_date: ord.order_date,
          is_product: ord.is_product,
          cig: ord.order_cig,
          cup: ord.order_cup
        });
      }

      // il for parte da 1 perché della prima fattura aggiungiamo prima i numeri di ordine, quindi qui dobbiamo fare il for solo i successivi
      for (let i = 1; i < this.analytics.length; i++) {
        const inv: any = this.analytics[i];

        this.selectableOrders.push({
          id: inv.order_id,
          parent: null,
          order_number: inv.order_number,
          description: inv.order_description,
          order_date: inv.order_date,
          is_product: inv.products && inv.products.length
        });

        for (const child of (inv.orders_child || [])) {
          this.selectableOrders.push({
            id: child.id,
            parent: inv.order_id,
            order_number: child.order_number,
            description: child.order_description,
            order_date: child.order_date,
            is_product: inv.products && inv.products.length
          });
        }
      }

      this.invoiceForm.patchValue({
        client_id: invoice.commission.client.id,
        payment_terms: invoice.payment_terms.id,
        invoice_type: invoice.invoice_type.id,
        vat_code: invoice.invoice_type.vat,
        has_client_approval: invoice.client_approval === null,
        cig: invoice.order_cig,
        cup: invoice.order_cup
      });

      this.expireInDays = invoice.payment_terms.days;
      let rows = [];

      for (const inv of this.analytics) {
        for (const user of inv.users) {
          rows.push({ ...user, month: inv.month, year: inv.year, month_name: inv.month_name, payment_measure_unit: inv.payment_measure_unit, order_description: inv.order_description, analytics_id: user.analytics_id });
        }
        for (const product of inv.products) {
          rows.push({ product_id: product.id, ...product, month: inv.month, year: inv.year, month_name: inv.month_name, payment_measure_unit: inv.payment_measure_unit, order_description: inv.order_description, analytics_id: product.analytics_id || inv.analytics_id });
        }
      }

      rows = rows.sort((a: any, b: any) => a.month < b.month ? -1 : a.month > b.month ? 1 : 0);

      rows.map((row: any, index) => this.addRow(row, index));

      this.selectableOrders.push({
        id: invoice.order_id || invoice.order_product_id,
        order_number: invoice.order_number,
        parent: invoice.order_child_id,
        description: invoice.order_description,
        order_date: invoice.order_date,
        is_product: invoice.products.length > 0,
        cig: invoice.order_cig,
        cup: invoice.order_cup,
        residue: invoice.residue
      });

      this.calculateTotalAmount();

      this._invoiceService.ifHaveOneOrder(this.selectableOrders, this.invoiceForm);

      if(this.selectableOrders.length === 1){
        this.order_residue = this.selectableOrders[0].residue;
      }

      this.usersNames = this.usersNames.filter((name) => name);

    } else {
      this.addRow(null, 0);
      this._invoiceService.openLastRow();
      this.invoiceForm.patchValue({ has_client_approval: true });
    }

    this.parent_orders = this.selectableOrders.filter((a: any) => !a.parent);
    this.cdr.detectChanges();
  }

  addRow(values: any = null, id: number) {
    const row = this._invoiceService.buildRow(id, this.defaultRowType, this.defaultServiceTrack.id);

    if (values && values.analytics_id && !this.residual_count.find((r: any) => r.analytics === values.analytics_id)) {
      this.residual_count = [...this.residual_count, { analytics: values.analytics_id, residual: values.estimate_profit }];
    }

    if (values) {
      values.daily_rate = Number(values.daily_rate);

      let qty = values.worked_days || values.working_days;
      if (values.payment_measure_unit?.name === "Ore") {
        qty *= 8;
        values.daily_rate /= 8;
      } else if (values.payment_measure_unit?.name === "Numero") {
        qty = 1;
      }

      values.daily_rate = values.estimate_profit / qty;

      if (!!values.hash) {
        row.patchValue({
          user_hash: values.hash,
          product_id: null,
          with_product: false
        });
      } else {
        row.patchValue({
          user_hash: null,
          product_id: values.product_id,
          with_product: true
        });

        const find_row_type_product = this.rows_types.find((row: any) => row.other.use === "product");
        if (find_row_type_product) row.patchValue({ row_type: find_row_type_product.id });
      }

      row.patchValue({
        quantity: qty,
        price: values.daily_rate,
        payment_measure_unit: Number(values.payment_measure_unit.id),
        analytics_id: values.analytics_id,
        description: values.order_description,
        total_amount: values.estimate_profit,
        month_name: this.months[values.month - 1],
        month: values.month,
        year: values.year
      });

      this.usersNames.push(values.complete_name || values.name);
    }

    if (this.parent_orders.length === 1) {
      row.patchValue({ order_id: this.parent_orders[0].id });

      if (!values && this.parent_orders[0].is_product) {
        const find_row_type_product = this.rows_types.find((row: any) => row.other.use === "product");
        if (find_row_type_product) row.patchValue({ row_type: find_row_type_product.id });
        row.patchValue({ with_product: true });
      }
    }

    if (this.years.length === 1) {
      row.patchValue({ year: this.years[0] });
    }

    row.patchValue({ row_type: this.defaultRowType, service_track: this.defaultServiceTrack.id, vat_code: this.defaultServiceTrack.other.sales_vat });

    this.rows.push(row);

    if (!values) {
      this.usersNames.push("NEW_ROW");
    }

    this.cdr.detectChanges();
  }

  fieldHasError(field: string) {
    return this._invoiceService.fieldHasError(this.invoiceForm, field, this.showErrors);
  }

  selectAllToDelete() {
    const rows = this.isBlank ? this.rows.controls : this.rows.controls.filter((row) => !!row.value.analytics_id);
    this.rowsToManipulate = rows.map((row) => row.value.id);
  }

  toggleSelectRow(index: number) {
    if (this.rowsToManipulate.includes(index)) {
      this.rowsToManipulate = this.rowsToManipulate.filter((item) => item !== index);
    } else {
      this.rowsToManipulate.push(index);
    }
  }

  removeRows() {
    if (!this.rowsToManipulate.length) return false;

    const controls = this.rows.controls;
    let names = JSON.parse(JSON.stringify(this.usersNames));

    this.rowsToManipulate.map((row) => {
      const findIndex = controls.findIndex((row2: any) => Number(row2.value.id) === row);
      this.rows.removeAt(findIndex);
      names = names.filter((name, i) => i !== findIndex);
    });

    this.rowsToManipulate = [];
    this.usersNames = names;

    setTimeout(() => this.calculateTotalAmount());
  }

  get rows() {
    return this.invoiceForm.controls.rows as FormArray;
  }

  handlerChangeDocumentType() {
    this._invoiceService.handlerChangeDocumentType(this.invoiceForm, this.analytics[0]);
  }

  handlerChangePaymentTerms() {
    const paymentTerm: any = this.payment_terms.find((pt: any) => pt.id === this.invoiceForm.value.payment_terms);
    this.expireInDays = paymentTerm?.other?.day_value || 30;
    if (!this.invoiceForm.value.document_date) return;
    this.handlerChangeDocumentDate();
  }

  handlerChangeDocumentDate() {
    const date: moment.Moment = utc(this.invoiceForm.value.document_date);
    date.add(this.expireInDays / 30, "months");
    const paymentTerm: any = this.payment_terms.find((pt: any) => pt.id === this.invoiceForm.value.payment_terms);
    if (paymentTerm && paymentTerm.other.payment_end_month) {
      date.endOf("month");
    }
    // aggiorno forzatamente expiration_date
    setTimeout(() => {
      const boxExpireDate: any = document.querySelector("#expiration_date input");
      if (boxExpireDate) boxExpireDate.value = date.format("DD-MM-YYYY");
      this.invoiceForm.patchValue({ expiration_date: date.format("YYYY-MM-DD"), accrual_exercise: moment(this.invoiceForm.value.document_date).year() });
    });
    this.cdr.detectChanges();
  }

  calculateTotalAmount() {
    this.invoiceForm.patchValue({ total_amount: this._invoiceService.calculateTotalAmount(this.rows) });
  }

  async setOrder() {
    const ordNumber = this.invoiceForm.value.order_number;
    const refOrder: any = this.selectableOrders.find((order: any) => order.order_number === ordNumber);

    if (refOrder) {
      let updatedValues: any = {
        cig: refOrder.cig,
        cup: refOrder.cup
      };
      if (refOrder.parent) {
        updatedValues.order_id = refOrder.is_product ? null : refOrder.parent;
        updatedValues.order_product_id = refOrder.is_product ? refOrder.parent : null;
        updatedValues.order_child_id = refOrder.id;
        updatedValues.order_date = refOrder.order_date ? moment(refOrder.order_date).format("YYYY-MM-DD") : null;
      } else {
        updatedValues.order_id = refOrder.is_product ? null : refOrder.id;
        updatedValues.order_product_id = refOrder.is_product ? refOrder.id : null;
        updatedValues.order_child_id = null;
        updatedValues.order_date = refOrder.order_date ? moment(refOrder.order_date).format("YYYY-MM-DD") : null;
      }
      for (const row of this.rows.controls) {
        row.patchValue({ description: refOrder.description });
      }
      this.order_residue = Number(refOrder.residue);
      this.invoiceForm.patchValue(updatedValues);
    } else {
      const invoice = this.analytics[0];

      this.invoiceForm.patchValue({
        order_id: invoice.order_id,
        order_product_id: null,
        order_child_id: null,
        order_date: invoice.order_date ? moment(invoice.order_date).format("YYYY-MM-DD") : null
      });

      for (const row of this.rows.controls) {
        row.patchValue({ description: invoice.order_description });
      }
    }

    if (!this.analytics.length) {
      const count_rows_with_analytics = this.rows.controls.filter((r: any) => !!r.value.analytics_id).length;
      if (count_rows_with_analytics > 0) {
        for (const row of this.rows.controls) {
          row.patchValue({ analytics_id: null, order_id: null });
        }
        await this._utilsService.showToastrSuccess("INVOICES_LINES_DISCONNECTED_FROM_ANALYTICS");
      }
      const count_rows_with_order = this.rows.controls.filter((r: any) => !!r.value.order_id).length;
      if (count_rows_with_order > 0) {
        for (const row of this.rows.controls) {
          row.patchValue({ order_id: null });
        }
        await this._utilsService.showToastrSuccess("INVOICES_LINES_ORDER_RESET");
      }
    }

    this.invoiceForm.patchValue({ invoice_order_number: this.invoiceForm.value.order_number });
  }

  removeRow(analytics_id: number) {
    // se user è un array allora sto eliminando una riga raggruppata
    const index = this.rows.controls.findIndex((row: any) => row.value.analytics_id === analytics_id && !row.value.has_parent && !row.value.childs);

    this.rows.removeAt(index);
    this.usersNames = this.usersNames.filter((name, i) => i !== index);

    this.calculateTotalAmount();
  }

  getAnalyticsId(data: any) {
    this.canAddRow = true;
    if (!this.residual_count.find((r: any) => r.analytics === data.analytics_id)) {
      this.residual_count = [...this.residual_count, { analytics: data.analytics_id, residual: data.estimate_profit }];
    }
  }

  async compressInOneLine() {
    const { controls } = this.rows;

    const selectedRows: any[] = controls.filter((row) => this.rowsToManipulate.includes(Number(row.value.id)))
      .map((row) => row.value);

    if (!selectedRows.length) return false;

    const countProducts = selectedRows.filter((row: any) => !!row.product_id && !row.has_parent).length;
    const countEmployees = selectedRows.filter((row: any) => !!row.user_hash && !row.has_parent).length;

    if (countEmployees > 0 && countProducts > 0) {
      await this._utilsService.showSWALInfo({ title: "ALERT_TITLE", text: "YOU_CAN_AGGREGATE_ONLY_POTATOES_WITH_POTATOES" });
      return false;
    }

    const unit = this.payment_measure_units.find((u: any) => u?.name === "Numero");

    const newRow = this._invoiceService
      .buildMergedRow(
        this.rows.controls.length,
        this.defaultRowType,
        this.defaultServiceTrack,
        unit.id || null,
        selectedRows.map((row: any) => row.id)
      );

    if (countProducts > 0) {
      const row_type_product = this.rows_types.find((type: any) => type.other.use === "product");
      newRow.patchValue({ row_type: row_type_product.id, with_product: true });
    }

    for (const row of selectedRows) {
      // imposto la riga come figlia
      let findRow = controls.find((row2: any) => row2.value.id === row.id);
      if (!findRow) continue;
      findRow.patchValue({ has_parent: true });

      newRow.patchValue({ price: Number(newRow.value.price) + Number(row.total_amount) });

      if (!!row.user_hash) {
        newRow.patchValue({ user_hash: [...(newRow.value.user_hash || []), row.user_hash] });
      }

      if (!!row.product_id) {
        newRow.patchValue({ product_id: [...(newRow.value.product_id || []), row.product_id] });
      }
    }

    newRow.patchValue({ description: this.rows.controls[0].value.description, total_amount: newRow.value.price });

    this.usersNames.push("GROUPED_ROWS");

    this.rows.push(newRow);

    this.rowsToManipulate = [];

    this._invoiceService.openLastRow();

    setTimeout(() => this.calculateTotalAmount());
  }

  removeRowGrouped(values: any) {
    this.rows.controls.filter((row) => values.childs.includes(row.value.id))
      .map((childRow) => {
        childRow.patchValue({ has_parent: false });
      });
    const parentRowIndex = this.rows.controls.findIndex((row: any) => row.value.childs === values.childs);

    this.rows.removeAt(parentRowIndex);
    this.usersNames = this.usersNames.filter((name, i) => i !== parentRowIndex);

    this.calculateTotalAmount();
  }

  async handlerAction() {
    this.showErrors = true;
    this.clickedOnSave = true;

    if (!!this.invoiceForm.value.order_product_id) {
      this.invoiceForm.get("order_id").setValidators(null);
      this.invoiceForm.get("order_id").updateValueAndValidity();
    } else {
      this.invoiceForm.get("order_product_id").setValidators(null);
      this.invoiceForm.get("order_product_id").updateValueAndValidity();
    }

    // controllo valori
    if (this.invoiceForm.invalid) {
      for (const key of Object.keys(this.invoiceForm.controls)) {
        if (key === "rows") continue;
        if (this.invoiceForm.controls[key].status !== "VALID") {
          console.warn(`${key} is invalid`);
          const input: any = document.querySelector(`*[formcontrolname="${key}"]`);
          if (input) {
            input?.scrollIntoView({ behavior: "smooth", block: "start" });
            input?.focus();
          }
        }
      }

      for (const key of Object.keys(this.rows.controls)) {
        if (this.rows.controls[key].status !== "VALID") {
          console.warn(`${key} is invalid`);
        }
      }
      this.clickedOnSave = false;
      return false;
    }

    // controllo che non ci siano righe senza analytics id
    const count_rows_without_analytics = this.rows.value.filter((item: any) => item.analytics_id == null && item.childs == null);
    if (!this.isBlank && count_rows_without_analytics.length > 0) {
      await this._utilsService.showToastrError("A_FEW_LINES_ARE_MISSING_THE_LINK_TO_ANALYTICS");
      return false;
    }

    const order_date = this.invoiceForm.value.order_date ? moment(this.invoiceForm.value.order_date).format("DD/MM/YYYY") : null;

    const values: any = {
      ...this.invoiceForm.value,
      is_blank: this.isBlank,
      rows: this.invoiceForm.value.rows.filter((row: any) => row !== null),
      additional_data: `[DatiOrdineAcquisto|IdDocumento|${this.invoiceForm.value.invoice_order_number || 0}];[DatiOrdineAcquisto|Data|${order_date}]`,
      attachments: this.attachments_list || []
    };

    delete values.order_number;
    delete values.order_date;

    const totalMissingAmount: number = await this._invoiceService.manageResidue(values.rows, this.residual_count);

    if (totalMissingAmount > 0) {
      const confirmationText: string = await this._utilsService.translate("ALERT.MISSING_INVOICE_AMOUNT")
        .then((text: string) => text.replace("[AMOUNT]", totalMissingAmount.toFixed(2)));

      await this._utilsService.showSWALDecision({
        text: confirmationText,
        confirmButtonText: "YES",
        cancelButtonText: "NO",
        customClass: {
          confirmButton: "btn btn-success",
          cancelButton: "btn btn-danger"
        }
      }).then(async (result) => {
        if (result.isDismissed) {
          for (let i = 0; i < values.rows.length; i++) {
            values.rows[i].invoice_residue = 0;
          }
        }
      });
    }

    this.clickedOnSave = false;

    this._utilsService.showLoading();
    this._invoiceService.insert(values).subscribe((result) => {
      if (result && result.id && result.id > 0) {
        this.router.navigateByUrl("/dashboard/invoice/to-generate");
        this._utilsService.showToastrSuccess("SUCCESS_INSERT_INVOICE");
      }
    });
  }

  async getClientOrders() {
    this.selectableOrders = await this._invoiceService.getClientOrders(this.invoiceForm);
    this.cdr.detectChanges();
  }
}
